import React from 'react'
import { MatchWithPredictions } from '../types/match'
import { getMatchDate, formatDate } from '../utils/dateHelper'
import { formatPoints } from '../utils/points-label'
import { useTranslation } from '../hooks/use-i18n'
import TeamNameWithFlag from './TeamNameWithFlag'

interface MatchCardProps {
  match: MatchWithPredictions
  roomId: string
}

const MatchCard: React.FC<MatchCardProps> = ({ match, roomId }) => {
  const { t } = useTranslation()
  // Determine match status from available fields
  const getMatchStatus = () => {
    if (match.score && match.winner) {
      return 'finished'
    } else if (match.score && !match.winner) {
      return 'live'
    } else {
      return 'upcoming'
    }
  }

  const matchStatus = getMatchStatus()

  // Removed the local formatDate function - using the imported one from dateHelper

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-blue-500'
      case 'live':
        return 'bg-red-500 animate-pulse'
      case 'finished':
        return 'bg-green-500'
      default:
        return 'bg-gray-500'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'Upcoming'
      case 'live':
        return 'Live'
      case 'finished':
        return 'Finished'
      default:
        return status
    }
  }

  const hasPrediction = match.userPrediction

  return (
    <>
      <div className="blue-sky-card rounded-2xl p-6 transform hover:scale-105 transition-all duration-300">
        {/* Match Header */}
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center flex-wrap gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(matchStatus)}`}>
              {getStatusText(matchStatus)}
            </span>
            {match.playoff && (
              <span className="px-3 py-1 rounded-full text-xs font-medium text-white bg-amber-500/90">
                Playoff
              </span>
            )}
            <span className="text-white/60 text-sm">{match.league.toUpperCase()}</span>
          </div>
          <div className="text-white/60 text-sm">
            {formatDate(getMatchDate(match, true), true)}
          </div>
        </div>

        {/* Teams */}
        <div className="flex items-center justify-between mb-6">
          <div className="text-center flex-1">
            <div className="text-lg font-semibold text-white mb-2">
              <TeamNameWithFlag teamName={match.teamA} league={match.league} side="home" nameClassName="text-lg font-semibold text-white" />
            </div>
            <div className="text-3xl font-bold text-sky-400">
              {match.score ? (() => {
                const [scoreA] = match.score.split('-').map(s => s.trim())
                return scoreA || '-'
              })() : '-'}
            </div>
            {match.score && (() => {
              const [scoreA, scoreB] = match.score.split('-').map(s => parseInt(s.trim() || '0', 10))
              // Determine winner from score if match.winner is not set correctly
              let actualWinner = match.winner
              if (!actualWinner || 
                  (actualWinner !== match.teamA && actualWinner !== match.teamB && actualWinner !== null)) {
                if (scoreA > scoreB) {
                  actualWinner = match.teamA
                } else if (scoreB > scoreA) {
                  actualWinner = match.teamB
                } else {
                  actualWinner = null
                }
              }
              // Use string comparison for safety
              const winnerStr = actualWinner ? String(actualWinner) : null
              const teamAStr = String(match.teamA)
              return winnerStr === teamAStr ? (
                <div className="text-xs text-green-400 mt-1">Winner</div>
              ) : null
            })()}
          </div>
          
          <div className="text-center mx-8">
            <div className="text-white/60 text-sm mb-1">VS</div>
            <div className="w-8 h-0.5 bg-white/30"></div>
          </div>
          
          <div className="text-center flex-1">
            <div className="text-lg font-semibold text-white mb-2">
              <TeamNameWithFlag teamName={match.teamB} league={match.league} side="away" nameClassName="text-lg font-semibold text-white" />
            </div>
            <div className="text-3xl font-bold text-sky-400">
              {match.score ? (() => {
                const [, scoreB] = match.score.split('-').map(s => parseInt(s.trim() || '0', 10))
                return scoreB || '-'
              })() : '-'}
            </div>
            {match.score && (() => {
              const [scoreA, scoreB] = match.score.split('-').map(s => parseInt(s.trim() || '0', 10))
              // Determine winner from score if match.winner is not set correctly
              let actualWinner = match.winner
              if (!actualWinner || 
                  (actualWinner !== match.teamA && actualWinner !== match.teamB && actualWinner !== null)) {
                if (scoreA > scoreB) {
                  actualWinner = match.teamA
                } else if (scoreB > scoreA) {
                  actualWinner = match.teamB
                } else {
                  actualWinner = null
                }
              }
              // Use string comparison for safety
              const winnerStr = actualWinner ? String(actualWinner) : null
              const teamBStr = String(match.teamB)
              if (winnerStr === teamBStr) {
                return <div className="text-xs text-green-400 mt-1">Winner</div>
              } else if (winnerStr === null || winnerStr === 'null') {
                return <div className="text-xs text-yellow-400 mt-1">Draw</div>
              }
              return null
            })()}
          </div>
        </div>

        {/* User Prediction */}
        {hasPrediction && match.userPrediction && (
          <div className="mb-6 p-4 bg-white/10 rounded-xl">
            <div className="text-white/80 text-sm mb-2">Your Prediction:</div>
            <div className="flex items-center justify-center space-x-4">
              <span className="text-white font-semibold">
                {match.userPrediction.predictedScore}
              </span>
              {(match.userPrediction.predictedWinnerPoint || 0) + (match.userPrediction.predictedScorePoint || 0) > 0 && (
                <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                  +{formatPoints((match.userPrediction.predictedWinnerPoint || 0) + (match.userPrediction.predictedScorePoint || 0), t)}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Match Status */}
        <div className="flex justify-center">
          <div className="text-white/60 text-sm">
            {matchStatus === 'finished' ? 'Match finished' : 
             matchStatus === 'live' ? 'Match in progress' : 
             'Upcoming match'}
          </div>
        </div>

        {/* Predictions Count */}
        {match.predictions.length > 0 && (
          <div className="mt-4 text-center">
            <span className="text-white/60 text-sm">
              {match.predictions.length} prediction{match.predictions.length !== 1 ? 's' : ''} made
            </span>
          </div>
        )}
      </div>
    </>
  )
}

export default MatchCard
